import {DestroyRef, ElementRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Store} from '@ngrx/store';
import {combineLatest, interval} from 'rxjs';
import {isPopulatedArray, isPopulatedString, isUndefined} from '@aviary/store/common/typing.helpers';
import {withLatestFromSelectors} from '@store/common/effects.helpers';
import {RegistrationActions} from '@store/registration/registration.actions';
import {RegistrationSelectors} from '@store/registration/registration.selectors';
import {POLL_VERIFICATION_STATUS_TIME_INTERVAL} from '@store/registration/registration.types';
import {ViewportSelectors} from '@aviary/store/viewport/viewport.selectors';

export function pollVerificationStatus(store: Store, destroyRef: DestroyRef): void {
  interval(POLL_VERIFICATION_STATUS_TIME_INTERVAL).pipe(
    withLatestFromSelectors(
      RegistrationSelectors.Registration.verificationUUID,
      RegistrationSelectors.Registration.isComplete('verifyEmail'),
      RegistrationSelectors.Registration.verificationExpired
    ),
    takeUntilDestroyed(destroyRef)
  ).subscribe(([, verificationUUID, verifyComplete, verificationExpired]) => {
    if (isPopulatedString(verificationUUID) && !verifyComplete && !verificationExpired) {
      store.dispatch(RegistrationActions.checkVerificationStatus(verificationUUID));
    }
  });
}

export function updateHeaderElements(elementRef: ElementRef<HTMLElement> | undefined, store: Store, destroyRef: DestroyRef, forLookupContext: boolean) {
  const headerElements: HTMLElement[] = getHorizontalStepperHeaderElements(elementRef);
  disableStepperKeyboardNavigation(headerElements);
  updateHeaderElementsVisibility(headerElements, store, destroyRef, forLookupContext);
}

export function getHorizontalStepperHeaderElements(elementRef: ElementRef<HTMLElement> | undefined): HTMLElement[] {
  if (isUndefined(elementRef)) return [];
  return Array.from(elementRef.nativeElement.querySelectorAll<HTMLElement>(
    '.mat-horizontal-stepper-header-container>.mat-step-header,.mat-horizontal-stepper-header-container>.mat-stepper-horizontal-line'
  ));
}

export function disableStepperKeyboardNavigation(elements: HTMLElement[]): void {
  elements.forEach((element) => {
    if (element.classList.contains('mat-step-header')) {
      element.setAttribute('tabindex', '-1');
      element.setAttribute('aria-disabled', 'true');
    }
  });
}

export function updateHeaderElementsVisibility(elements: HTMLElement[], store: Store, destroyRef: DestroyRef, forLookupContext: boolean): void {
  if (!isPopulatedArray(elements)) return;
  combineLatest([
    store.select(ViewportSelectors.isSm),
    store.select(forLookupContext ? RegistrationSelectors.Registration.currentLookupAccountStep : RegistrationSelectors.Registration.currentCreateAccountStep)
  ]).pipe(takeUntilDestroyed(destroyRef)).subscribe(([isSm, currentStep]) => setHeaderElementsVisibility(elements, isSm, currentStep));
}

export function setHeaderElementsVisibility(elements: HTMLElement[], isSm: boolean, currentStep: number): void {
  if (!isPopulatedArray(elements)) return;
  // elements has an HTML element for each step header and each line between them, the length should always be odd
  const numberOfSteps: number = (elements.length + 1) / 2;
  // numberOfSteps should be 6 or 4 depending on create account or lookup context respectively, but at least 4 is needed to keep this clamping in bounds
  if (numberOfSteps < 4) return;
  const visibleSteps: boolean[] = new Array(numberOfSteps);
  if (isSm) {
    // the step before and after centeredStep will be visible for a 3 step moving window
    // this means centeredStep can't be the first or the second to last element, the last element is the final completed step which is always hidden
    let centeredStep = currentStep;
    if (centeredStep < 1) centeredStep = 1;
    if (centeredStep > numberOfSteps - 3) centeredStep = numberOfSteps - 3;
    visibleSteps.fill(false);
    visibleSteps[centeredStep - 1] = visibleSteps[centeredStep] = visibleSteps[centeredStep + 1] = true;
  } else {
    visibleSteps.fill(true);
    visibleSteps[numberOfSteps - 1] = false; // always hide final complete step
  }
  const firstVisibleStep = visibleSteps.indexOf(true);
  const lastVisibleStep = visibleSteps.lastIndexOf(true);
  let elementsIndex: number = 0;
  visibleSteps.forEach((isVisible: boolean, index: number) => {
    if (index !== 0) {
      // update visibility for the horizontal line element before this step
      elements[elementsIndex++].classList.toggle('hidden', !isVisible || index <= firstVisibleStep);
    }
    // update visibility for this step's header
    elements[elementsIndex].classList.toggle('mx-hide-before-line', index <= firstVisibleStep);
    elements[elementsIndex].classList.toggle('mx-hide-after-line', index >= lastVisibleStep);
    elements[elementsIndex++].classList.toggle('hidden', !isVisible);
  });
}
